import React from 'react';

import { widgetsConfigurations } from 'config/widgetConfigurations/widgetsConfigurations';
import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { ColumnsManager } from 'components/common/ColumnsManager/ColumnsManager';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { List } from 'components/common/ListWrapper/list';
import NoData from 'components/common/NoData/noData';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { TableContainer } from 'components/common/Tables/components/TableContainer/TableContainer';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetExistInBackend } from 'components/common/WidgetExistInBackend/WidgetExistInBackend';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { TableFooter } from 'components/Dashboards/Project/Components/VersionCompare/TableFotter/TableFooter';
import { smallPageSize } from 'components/Dashboards/Project/Components/VersionCompare/utils/table.utils';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { ACTIVITY_TYPE_FILTERS, TIME_FILTERS } from 'components/widgets/KeyAchievements/keyAchievements.consts';
import { useKeyAchievementsLogic } from 'components/widgets/KeyAchievements/keyAchievements.logic';
import classes from 'components/widgets/KeyAchievements/keyAchievements.module.scss';
import { useKeyAchievementsProvider } from 'components/widgets/KeyAchievements/keyAchievements.provider';
import { columnsV8, initialState } from 'components/widgets/KeyAchievements/keyAchievements.utils';

const { componentKey, id, endpoint } = widgetsConfigurations.keyAchievements;

export const KeyAchievements = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    compareVersionId,
    globalFilterData,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    localFilters,
    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title = widgetsConfigurations.keyAchievements.title,
    setIsPayloadReady,
}: IComponentProps) => {
    // Table
    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        initialState,
        columns: columnsV8,
        initialPagination: { page: 1, size: 5 },
        widgetViewMode,
    });

    // Provider
    const { ui, api } = useKeyAchievementsProvider({
        componentKey,
        widgetId,
        globalFilterData,
        localFilters,
        table,
        compareVersionId,
        latestVersionId,
    });

    // Widget
    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        title,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        onAddWidgetCallback: table.setWidgetColumns,
        apiPayload: api.getKeyAchievementsData.payload,
        widgetEndpoint: endpoint,
    });

    // Logic
    const { logic } = useKeyAchievementsLogic({
        ui,
        api,
        title,
        subTitle,
        table,
        setIsPayloadReady,
        widgetViewMode,
    });

    return (
        <WidgetExistInBackend componentKey={componentKey}>
            <WidgetWithTitle
                title={
                    <WidgetTitle
                        title={editableTitle}
                        setTitle={setEditableTitle}
                        widgetViewMode={widgetViewMode}
                        postfix={`(${logic.tableData.length})`}
                    />
                }
                className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
                id={id}
                titleFilters={[
                    <ReportingVersionAndPills
                        key={'ReportingVersionAndPills'}
                        selectedProjectAndContract={widgetSelectedProjectAndContract}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                    <Narrative key={'narrative'} editNarrative={editNarrative} />,
                    <PinboardPills
                        key={'pinboardPills'}
                        isInPinboard={WidgetViewModeUtils.isPinboardView(widgetViewMode)}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                ]}
                titleComponents={[
                    <div className={classes.listWrapper}>
                        {!ui.isInPinboard && (
                            <div className={classes.filtersWrapper}>
                                <div className={classes.filterTitle}>Last:</div>
                                <List<string>
                                    onChange={logic.handleLastFilterClick}
                                    options={TIME_FILTERS}
                                    value={logic.timePeriodFilterValue}
                                />
                            </div>
                        )}
                        {!ui.isInPinboard && (
                            <div className={classes.filtersWrapper}>
                                <div className={classes.filterTitle}>Activity type:</div>
                                <List<string>
                                    onChange={logic.handleActivityTypeFilterClick}
                                    options={ACTIVITY_TYPE_FILTERS}
                                    value={logic.selectedActivityTypeFilter}
                                />
                            </div>
                        )}
                    </div>,
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    <AddToReport
                        key={'addWidgetToReport'}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        projectId={projectId}
                        contractId={contractId}
                        uiState={ui.state}
                        callBack={table.setWidgetColumns}
                        shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                        apiPayload={api.getKeyAchievementsData.payload}
                        widgetEndpoint={endpoint}
                    />,
                    !WidgetViewModeUtils.isReportView(widgetViewMode) && (
                        <ColumnsManager
                            onOpenModal={() => table.setModalColumnsOpen(true)}
                            onHideModal={() => table.setModalColumnsOpen(false)}
                            showModal={table.modalColumnsOpen}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            tableInstance={table.tableInstance}
                            initialState={initialState}
                            columns={columnsV8}
                            widgetViewMode={widgetViewMode}
                        />
                    ),
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ExportDropdown key={'export'} title={title} subTitle={subTitle} />
                    ),
                    <TooltipIcon key={'info'} tooltip={api.getKeyAchievementsData.instance.data?.info} />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ThreeDotsMenu
                            widgetViewMode={widgetViewMode}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            shareTitle={title}
                            shareLink={route}
                        />
                    ),
                ]}
            >
                <TableContainer>
                    <TableV8
                        data={logic.tableData}
                        columns={columnsV8}
                        isLoading={
                            api.getKeyAchievementsData.instance.isLoading ||
                            api.getKeyAchievementsData.instance.isFetching
                        }
                        noData={<NoData>No Changes To Display</NoData>}
                        sorting={table.sorting}
                        setSorting={table.setSorting}
                        columnVisibility={table.columnVisibility}
                        setColumnVisibility={table.setColumnVisibility}
                        columnOrder={table.columnOrder}
                        setColumnOrder={table.setColumnOrder}
                        manualPagination={false}
                        manualSorting={false}
                        pagination={table.pagination}
                        rowSelection={table.rowSelection}
                        setRowSelection={table.setRowSelection}
                    />

                    {logic.tableData.length > smallPageSize && (
                        <TableFooter
                            setPageSize={(pageSize) => {
                                table.setPagination({ ...table.pagination, size: pageSize });
                            }}
                            pageSize={table.pagination.size}
                        />
                    )}
                </TableContainer>
            </WidgetWithTitle>
        </WidgetExistInBackend>
    );
};
